import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { IInfluencer } from '../../redux/buzzbridge/types';
import { getPublicInfluencerProfile } from '../../redux/buzzbridge/actions';
import {
    gettingPublicInfluencerProfileSelector,
    publicInfluencerProfileErrorSelector,
    publicInfluencerProfileSelector,
    publicInfluencerProfileUserNameSelector,
} from '../../redux/buzzbridge/selectors';
import { normalizePublicProfileUserName } from '../../services/influencerPublicProfile';

function publicProfileErrorMessage(error: unknown): string {
    if (!error || typeof error !== 'object') return 'Profile not available';
    const e = error as { data?: { message?: string }; message?: string };
    return e.data?.message || e.message || 'Profile not available';
}

export function usePublicInfluencerProfile(userName: string | undefined, initialData?: IInfluencer | null) {
    const dispatch = useDispatch();
    const profile = useSelector(publicInfluencerProfileSelector);
    const loadedFor = useSelector(publicInfluencerProfileUserNameSelector);
    const loading = useSelector(gettingPublicInfluencerProfileSelector);
    const profileError = useSelector(publicInfluencerProfileErrorSelector);

    const normalizedUserName = userName ? normalizePublicProfileUserName(userName) : '';
    const matchesRequest = Boolean(normalizedUserName && loadedFor === normalizedUserName);

    useEffect(() => {
        if (!normalizedUserName) return;
        dispatch(getPublicInfluencerProfile({ userName: normalizedUserName }));
    }, [dispatch, normalizedUserName]);

    const influencer = useMemo(() => {
        if (matchesRequest && profile) return profile;
        if (loading && initialData) return initialData;
        return initialData ?? null;
    }, [matchesRequest, profile, loading, initialData]);

    const error = matchesRequest && profileError ? publicProfileErrorMessage(profileError) : null;
    const isLoading = Boolean(normalizedUserName && loading && loadedFor === normalizedUserName);

    return { influencer, loading: isLoading, error };
}
